import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { supabase } from '../lib/supabase';
import { fetchCruiseTimes, fetchBoardingPoint } from '../lib/cruiseDetails';
import { formatCardDate, displayDeck, to12h, cruiseLabel } from '../components/shared/helpers';
import { downloadTicket } from '../utils/ticketPDF';
import { downloadInvoice } from '../utils/invoicePDF';
import { trackEvent, trackMeta, cruiseParams, bookingValue, paidSeats, CURRENCY } from '../lib/analytics';

// booking_completed (GA4) and Purchase (Meta) must fire exactly once per
// booking. Module-level so a StrictMode double-effect or a remount of this
// screen can't re-send either.
const reportedBookings = new Set();

export default function Confirmation() {
  const { selectedDate, passengers, selectedCruise, bookingRef, paidAmount, fullName, phone, fading } = useBooking();

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const [invoiceDownloading, setInvoiceDownloading] = useState(false);
  const [invoiceDownloadError, setInvoiceDownloadError] = useState('');

  // Boarding point via the shared cruiseDetails lookup (for the cruise date),
  // so it matches the rest of the site rather than being hardcoded here.
  const [boardingPoint, setBoardingPoint] = useState('');

  // Reporting time via the same lookup the ticket prints from, so the two can
  // never disagree. Seeded from the availability payload so the row is filled
  // on first paint, then refreshed in case the schedule changed since the
  // cruise was picked.
  const [reportingTime, setReportingTime] = useState(() => to12h(selectedCruise?.reportingTime));
  const cruiseCode = selectedCruise?.cruiseCode;

  useEffect(() => {
    if (!cruiseCode) return;
    let cancelled = false;
    (async () => {
      const times = await fetchCruiseTimes({ cruiseCode, date: selectedDate });
      const fresh = to12h(times?.reporting_time);
      if (!cancelled && fresh) setReportingTime(fresh);
    })();
    return () => { cancelled = true; };
  }, [cruiseCode, selectedDate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const boarding = await fetchBoardingPoint({ date: selectedDate });
      if (!cancelled && boarding?.boarding_point) setBoardingPoint(boarding.boarding_point);
    })();
    return () => { cancelled = true; };
  }, [selectedDate]);


  // Funnel step 6 (the conversion) — fires on arrival here with the booking
  // value. transaction_id is the booking reference (not personal data).
  //
  // Meta Purchase rides the same guard with eventID = the booking reference,
  // so a later Conversions API event for this booking can be deduplicated
  // against it. Its value is the amount actually charged (the server-created
  // order, stored in context by Payment.js); the display price is only the
  // fallback if the amount is somehow missing.
  useEffect(() => {
    if (!bookingRef || !selectedCruise || reportedBookings.has(bookingRef)) return;
    reportedBookings.add(bookingRef);
    trackEvent('booking_completed', {
      transaction_id: bookingRef,
      value: bookingValue(selectedCruise, passengers),
      currency: CURRENCY,
      ...cruiseParams(selectedCruise, passengers),
    });
    const paid = Number.isFinite(paidAmount) && paidAmount > 0
      ? paidAmount
      : bookingValue(selectedCruise, passengers);
    trackMeta(
      'Purchase',
      {
        value: paid,
        currency: CURRENCY,
        content_name: selectedCruise?.cruiseName || '',
        num_items: paidSeats(passengers),
      },
      { eventID: bookingRef },
    );
  }, [bookingRef, selectedCruise, passengers, paidAmount]);

  // Pricing derived from the selected cruise
  const priceAdult = selectedCruise?.priceAdult ?? 0;
  const priceChild = selectedCruise?.priceChild ?? 0;
  const adultsSubtotal = passengers.adults * priceAdult;
  const childrenSubtotal = passengers.children * priceChild;
  const grandTotal = adultsSubtotal + childrenSubtotal;

  // Passenger summary string (zero counts hidden)
  const paxSummary = [
    `${passengers.adults} ${passengers.adults === 1 ? 'Adult' : 'Adults'}`,
    passengers.children > 0 ? `${passengers.children} ${passengers.children === 1 ? 'Child' : 'Children'}` : null,
    passengers.infants > 0 ? `${passengers.infants} ${passengers.infants === 1 ? 'Infant' : 'Infants'}` : null,
  ].filter(Boolean).join(' · ');


  // Generate the boarding-pass PDF and trigger the browser save. downloadTicket
  // uses bookingRef as the preloadedReference (for the QR + reference number), so
  // booking.id can be null here — the generator never reads it when a reference
  // is supplied (and bookingRef is guaranteed present past the guard below).
  const handleDownload = async () => {
    if (downloading) return;
    setDownloadError('');
    setDownloading(true);
    try {
      // selectedDate is the booking date as ISO (YYYY-MM-DD); the ticket wants
      // DD-MM-YYYY, matching the conversion the other screens use.
      const iso = selectedDate;
      const [y, m, d] = String(iso || '').split('-');
      const dateDdMmYyyy = (y && m && d) ? `${d}-${m}-${y}` : '';

      const booking = {
        id: null,                          // unavailable here; generator uses bookingRef instead
        booking_name: fullName || 'Guest',
        cruise_type: selectedCruise?.cruiseName || '',
        deck: selectedCruise?.deckLabel || '',
        date: dateDdMmYyyy,
        adults: passengers.adults,
        children: passengers.children,
        infants: passengers.infants,
        tour_leaders: 0,
      };

      await downloadTicket(booking, bookingRef);
    } catch (err) {
      console.error('Ticket download failed:', err);
      setDownloadError("Couldn't prepare the download. Your ticket has also been sent to your WhatsApp.");
    } finally {
      setDownloading(false);
    }
  };

  // For B2C the invoice's invoice_number equals the booking reference. The
  // invoice auto-generates fire-and-forget after booking, so it can lag landing
  // here by a second or two — hence the brief retry loop.
  const handleDownloadInvoice = async () => {
    if (invoiceDownloading) return;
    setInvoiceDownloadError('');
    setInvoiceDownloading(true);
    try {
      // Invoices are RLS-locked; fetch via the get-invoice edge function, which
      // requires the booking phone as a second factor (bare 10-digit match).
      const normalizedPhone = String(phone || '').replace(/\D/g, '').slice(-10);
      let inv = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const { data: fnData } = await supabase.functions.invoke('get-invoice', {
          body: { invoiceNumber: bookingRef, phone: normalizedPhone },
        });
        if (fnData?.invoice) { inv = fnData.invoice; break; }
        if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 1200));
      }

      if (!inv) {
        setInvoiceDownloadError('Your invoice is being prepared and has been sent to your WhatsApp. Please check there in a few minutes.');
        return;
      }

      // Non-dinner invoices need per-passenger pricing to rebuild line items;
      // dinner invoices are self-contained (pricingRates = null).
      let pricingRates = null;
      if (!String(selectedCruise?.cruiseName || '').toLowerCase().includes('dinner')) {
        const { data: pricing } = await supabase
          .from('pricing')
          .select('passenger_type, price')
          .eq('cruise_type', selectedCruise.cruiseName)
          .eq('deck', selectedCruise.deckLabel);
        const rates = { adult: 0, child: 0 };
        (pricing || []).forEach((p) => {
          const t = String(p.passenger_type || '').toLowerCase();
          if (t === 'adult') rates.adult = Number(p.price) || 0;
          else if (t === 'child') rates.child = Number(p.price) || 0;
        });
        pricingRates = rates;
      }

      // Same ISO → DD-MM-YYYY conversion the ticket handler uses.
      const iso = selectedDate;
      const [y, m, d] = String(iso || '').split('-');
      const dateDdMmYyyy = (y && m && d) ? `${d}-${m}-${y}` : '';

      const booking = {
        cruise_type: selectedCruise?.cruiseName || '',
        deck: selectedCruise?.deckLabel || '',
        date: dateDdMmYyyy,
        adults: passengers.adults,
        children: passengers.children,
      };

      await downloadInvoice(inv, booking, null, pricingRates);
    } catch (err) {
      console.error('Invoice download failed:', err);
      setInvoiceDownloadError("Couldn't prepare the invoice. It has also been sent to your WhatsApp.");
    } finally {
      setInvoiceDownloading(false);
    }
  };

  // Guard — a booking reference must be present
  if (!bookingRef || !selectedCruise) return <Navigate to="/" replace />;

  return (
    <div className={`w-full max-w-[600px] mx-auto px-0 md:px-2 card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>

        {/* SECTION 1 — Success moment */}
        <div className="flex flex-col items-center text-center">
          <div className="check-pop mb-3 md:mb-3">
            <svg viewBox="0 0 52 52" className="w-[72px] h-[72px] md:w-16 md:h-16" aria-hidden="true">
              <circle cx="26" cy="26" r="24" fill="rgba(201,168,76,0.08)" stroke="#C9A84C" strokeWidth="2" />
              <path className="check-draw" d="M16 27 L23 34 L37 19" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="font-cormorant font-normal text-[2.4rem] md:text-[2.2rem] leading-[1.05] tracking-[-0.01em] text-white mb-1 md:mb-1.5">
            Booking Confirmed
          </h1>
          <p className="font-dm-sans text-sm md:text-[15px] font-medium text-[rgba(212,168,67,0.85)] mb-5 md:mb-5">
            See you on the Brahmaputra
          </p>
        </div>

        {/* SECTION 2 — Booking reference */}
        <div className="rounded-xl border border-[#C9A84C] bg-[#162840] px-5 py-5 md:py-3 mb-4 md:mb-4 text-center">
          <p className="font-dm-sans text-[13px] uppercase tracking-[0.22em] font-medium text-white/60 mb-2 md:mb-1">
            Booking Reference
          </p>
          <p className="font-cormorant text-[2rem] md:text-[2.1rem] leading-none text-[#C9A84C] tracking-[0.02em] mb-2.5 md:mb-1.5">
            {bookingRef}
          </p>
          <p className="font-dm-sans text-sm font-light text-white/55">
            Please keep this reference handy at boarding
          </p>
        </div>

        {/* SECTION 3 — Booking summary card */}
        <div className="rounded-xl border border-white/[0.09] bg-[#162840] p-4 md:p-3 mb-4 md:mb-4">
          {[
            ['Date', formatCardDate(selectedDate)],
            ['Cruise', cruiseLabel(selectedCruise)],
            ['Deck', displayDeck(selectedCruise.cruiseName, selectedCruise.deckLabel)],
            ['Time', selectedCruise.time],
            ['Reporting Time', reportingTime],
            ['Boarding', boardingPoint],
            ['Passengers', paxSummary],
          ].map(([label, value], i) => (
            <div key={label} className={`flex items-start justify-between gap-6 py-2.5 md:py-1.5 ${i > 0 ? 'border-t border-white/[0.06]' : ''}`}>
              <span className="font-dm-sans text-[13px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)] shrink-0">
                {label}
              </span>
              <span className={`font-dm-sans text-sm font-light text-white text-right leading-relaxed min-w-0 break-words ${label === 'Boarding' ? 'max-w-[62%]' : ''}`}>
                {value}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between gap-4 pt-3 md:pt-2 mt-1 md:mt-0.5 border-t border-[rgba(212,168,67,0.25)]">
            <span className="font-dm-sans text-sm font-medium text-white">Total Paid</span>
            <span className="font-dm-sans text-lg font-bold text-[#C9A84C] tabular-nums">&#8377;{grandTotal.toLocaleString()}</span>
          </div>
        </div>

        {/* SECTION 4 — Download ticket */}
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="w-full py-[15px] md:py-0 md:h-12 md:flex md:items-center md:justify-center bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.98] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
        >
          {downloading ? 'Preparing…' : 'Download Ticket'}
        </button>
        <p className="font-dm-sans text-center text-sm font-medium text-white/70 leading-relaxed mt-3 md:mt-2.5">
          Your ticket has been sent to your WhatsApp
        </p>
        <p className="font-dm-sans text-center text-sm font-light text-white/55 leading-relaxed mt-0.5">
          Please check your WhatsApp. Your ticket may take a few minutes to arrive.
        </p>
        {downloadError && (
          <p className="fade-in-soft font-dm-sans text-center text-sm text-[rgba(212,168,67,0.75)] mt-3 md:mt-2 leading-relaxed">
            {downloadError}
          </p>
        )}

        {/* SECTION 4b — Download invoice */}
        <button
          type="button"
          onClick={handleDownloadInvoice}
          disabled={invoiceDownloading}
          className="w-full mt-3 md:mt-2.5 py-[15px] md:py-0 md:h-12 md:flex md:items-center md:justify-center bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.98] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
        >
          {invoiceDownloading ? 'Preparing…' : 'Download Invoice'}
        </button>
        <p className="font-dm-sans text-center text-sm font-light text-white/55 leading-relaxed mt-3 md:mt-2.5">
          Your invoice has also been sent to your WhatsApp
        </p>
        {invoiceDownloadError && (
          <p className="fade-in-soft font-dm-sans text-center text-sm text-[rgba(212,168,67,0.75)] mt-3 md:mt-2 leading-relaxed">
            {invoiceDownloadError}
          </p>
        )}

        {/* SECTION 5 — Closing line */}
        <p className="font-dm-sans text-center text-sm font-light text-white/55 leading-relaxed mt-5 md:mt-3 mb-2 max-w-[420px] mx-auto">
          Thank you for choosing Alfresco Grand. We look forward to welcoming you aboard.
        </p>

      </div>
  );
}
